import { auth0 } from '@/helpers/Auth0Client'
import { environment } from '@/helpers/Environment'
import store from '@/store/store'
import { getCookie } from 'tiny-cookie'

type LoginAttemptResult = {
  success: boolean
  error?: Error
}

type ExternalAuthMethod = 'auth0' | 'other'

function externalLoginUrl() {
  return environment.web.external_urls?.login
}

function externalLogoutUrl() {
  return environment.web.external_urls?.logout
}

function getAuthMethod(): ExternalAuthMethod | undefined {
  if (auth0.isAuth0()) return 'auth0'
  if (externalLoginUrl()) return 'other'
}

function isExternalAuth() {
  return getAuthMethod() !== undefined
}

async function login(currentPath: string): Promise<LoginAttemptResult> {
  let result: LoginAttemptResult

  switch (getAuthMethod()) {
    case 'auth0':
      result = await auth0.login(currentPath)
      break
    case 'other': {
      const externalUrl = externalLoginUrl()
      if (externalUrl) {
        const cartId = store.getters['Cart/cartId']
        window.location.href = cartId ? `${externalUrl}?cartId=${cartId}` : externalUrl
      }
      result = { success: false }
      break
    }
    default:
      result = { success: false }
  }

  return result
}

async function logout(nextPath: string): Promise<LoginAttemptResult> {
  switch (getAuthMethod()) {
    case 'auth0': {
      const returnTo = window.location.origin + nextPath
      return auth0.logout(returnTo)
    }
    case 'other': {
      const externalUrl = externalLogoutUrl()
      if (externalUrl) window.location.href = externalUrl
      return { success: true }
    }
    default:
      return { success: false }
  }
}

async function handleRedirectCallback(): Promise<LoginAttemptResult> {
  let result: LoginAttemptResult

  switch (getAuthMethod()) {
    case 'auth0':
      result = await auth0.handleRedirectCallback()
      break
    default:
      result = { success: false, error: new Error('Unknown auth method') }
  }

  return result
}

/** Checks if the user has a session cookie but no identity, which means they have been logged in from outside the app. (e.g. MoS Boston, oauth web server flows) */
export function isLoggedInExternally(): boolean {
  const hasSessionCookie = getCookie('session_expires')
  const identity = store.getters['Member/identity']

  return Boolean(hasSessionCookie && !identity)
}

export const authExternal = {
  isExternalAuth,
  login,
  logout,
  externalLoginUrl,
  externalLogoutUrl,
  handleRedirectCallback,
  isLoggedInExternally,
}

if (window.Cypress) {
  window.__spyOn?.(authExternal)
}

export type AuthExternal = typeof authExternal

declare global {
  interface Window {
    __mockAuthExternal?: typeof authExternal
  }
}
